/** @jsx jsx */
import { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { Button, Classes, InputGroup, Intent, Spinner } from '@blueprintjs/core';
import { ColorAliases } from 'styles/variables';
import logo from '../../images/Serberus.svg';

/**
 *
 * @param {JSX.Element} Title
 * @param {function} authenticate
 * @param {string} error
 * @param {boolean} isLoading
 * @returns {JSX.Element}
 * @constructor
 */
export const LoginScreen = ({
	title: Title,
	authenticate,
	error,
	anonymous = false,
	projectLogo = null,
	projectLogoStyle = null,
}) => {
	const [userName, setUserName] = useState('');
	const [password, setPassword] = useState('');
	const [localError, setLocalError] = useState(error);
	const [localLoading, setLocalLoading] = useState(false);

	useEffect(() => {
		setLocalLoading(false);
		setLocalError(error);
	}, [error]);

	const onKeyDown = (event) => {
		if (event.keyCode === 13) {
			onClick();
		}
	};

	const onClick = () => {
		if (userName && password) {
			setLocalLoading(true);
			authenticate(userName, password);
			setPassword('');
		}
	};

	return (
		<div css={container}>
			<img
				src={projectLogo ? projectLogo : logo}
				alt="logo"
				css={projectLogoStyle ? projectLogoStyle : defaultLogoStyle}
			/>
			<div css={login}>
				<div css={titleStyle}>
					<Title />
				</div>
				{localLoading ? (
					<Loading />
				) : localError ? (
					<LocalError localError={localError} password={password} setLocalError={setLocalError} userName={userName} />
				) : (
					<UserPass
						onKeyDown={onKeyDown}
						userName={userName}
						password={password}
						setUserName={setUserName}
						setPassword={setPassword}
						onClick={onClick}
						anonymous={anonymous}
					/>
				)}
			</div>
		</div>
	);
};

const LocalError = ({ localError, setLocalError }) => {
	return (
		<div css={userpass}>
			<div cy-test="login-failed" css={failedStyle}>
				<div>Login Failed</div>
				<div>{localError.message}</div>
			</div>
			<Button onClick={() => setLocalError(null)} intent={Intent.DANGER} fill={true}>
				Retry
			</Button>
		</div>
	);
};

const UserPass = ({ onKeyDown, setUserName, userName, setPassword, password, onClick, anonymous }) => {
	if (anonymous) {
		setUserName('demo');
		setPassword('demo');
		onClick();
	}
	return (
		<div css={userpass}>
			<InputGroup
				cy-test="username"
				className={Classes.FILL}
				leftIcon="user"
				onKeyDown={onKeyDown}
				onChange={(event) => setUserName(event.target.value)}
				name="username"
				placeholder="Username"
				value={userName}
				autoFocus
			/>
			<InputGroup
				cy-test="password"
				className={Classes.FILL}
				leftIcon="lock"
				type="password"
				name="password"
				placeholder="Password"
				onKeyDown={onKeyDown}
				onChange={(event) => setPassword(event.target.value)}
				value={password}
			/>
			<Button
				cy-test="login-btn"
				onClick={onClick}
				disabled={!userName || !password}
				intent={Intent.PRIMARY}
				fill={true}
			>
				Login
			</Button>
		</div>
	);
};

const Loading = () => {
	return <Spinner />;
};

const container = css`
	display: flex;
	background-color: ${ColorAliases.PT_INTENT_PRIMARY};
`;

const titleStyle = css`
	text-align: center;
	flex: 0 0 auto;
	padding-bottom: 40px;
`;

const userpass = css`
	flex: 0 0 auto;
	width: 285px;
`;

const failedStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	font-size: 2.5rem;
	font-weight: bold;
	color: ${ColorAliases.PT_INTENT_DANGER};

	div:last-of-type {
		font-weight: lighter;
		font-size: 1.5rem;
	}
`;

const defaultLogoStyle = css`
	position: absolute;
	left: 20%;
	top: 25%;
	width: 60%;
	opacity: 0.2;
`;

const login = css`
	position: absolute;
	left: 0;
	right: 0;
	width: 400px;
	height: 100%;
	margin: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	div {
		margin-bottom: 10px;
	}
`;
